import { Button, Collapse, ListItemText, styled } from '@mui/material';
import { alpha } from '@mui/system';
import { Link } from 'react-router-dom';
import { DASHBOARD_DARK } from 'theme/dashboardDarkPalette';
import { ITheme } from 'theme/types';

const primaryScale = (theme: unknown, key: keyof ITheme['palette']['primary']) =>
  (theme as unknown as ITheme).palette.primary[key];

const StyledListItemXs = styled('li')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const accentColor = theme.palette.primary.main;
  return {
    color: isDark ? theme.palette.text.primary : theme.palette.common.white,
    cursor: 'pointer',
    padding: theme.spacing(2),

    '&>svg': {
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
      ...(isDark && { color: DASHBOARD_DARK.textSecondary }),
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },

    '&.active': {
      ...(isDark
        ? {
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          boxShadow: `inset 3px 0 0 0 ${accentColor}`,
          paddingLeft: `calc(${theme.spacing(2)} - 3px)`,
          '&>svg': { color: accentColor },
        }
        : {
          boxShadow: `inset 3px 0 0 0 ${theme.palette.common.white}`,
          paddingLeft: `calc(${theme.spacing(2)} - 3px)`,
        }),
    },
  };
});

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'capitalize',
  display: 'flex',
  '& .MuiTypography-body1': {
    fontWeight: 700,
    color: theme.palette.mode === 'dark' ? DASHBOARD_DARK.textPrimary : theme.palette.common.white,
  },
}));

const StyledListItemWithLink = styled('li')(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const accentColor = theme.palette.primary.main;
  return {
    cursor: 'pointer',
    padding: 0,

    '&.active': isDark
      ? {
        boxShadow: `inset 3px 0 0 0 ${accentColor}`,
        backgroundColor: alpha(accentColor, 0.10),
      }
      : {
        boxShadow: `inset 3px 0 0 0 ${theme.palette.common.white}`,
      },

    display: 'none',

    [theme.breakpoints.up('md')]: {
      display: 'block',
    },

    '& .MuiListItemIcon-root': {
      color: isDark ? DASHBOARD_DARK.textSecondary : 'rgba(255,255,255,0.75)',
    },
    '&.active .MuiListItemIcon-root': {
      color: isDark ? accentColor : theme.palette.common.white,
    },
  };
});

const StyledItemLink = styled(Button)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    width: '100%',
    cursor: 'pointer',
    padding: theme.spacing(2),
    borderRadius: 0,
    color: isDark ? DASHBOARD_DARK.textSecondary : 'rgba(255,255,255,0.75)',
    '&:hover': {
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.05)
        : (primaryScale(theme, '500') ?? theme.palette.primary.main),
      color: theme.palette.common.white,
    },
  };
});

const StyledListItem = styled('li')(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? DASHBOARD_DARK.textPrimary : theme.palette.common.white,
  cursor: 'pointer',
  padding: theme.spacing(2),

  '&>svg': {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(primaryScale(theme, '500') ?? theme.palette.primary.main, 0.2),

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const StyledSubItem = styled('li')(({ theme }) => ({
  borderRadius: 0,
  padding: 0,
  margin: 0,
  '& .MuiListItemIcon-root': {
    minWidth: theme.spacing(5),
  },
  '&.Mui-selected': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.14)
        : primaryScale(theme, '200'),
  },
  '&.Mui-selected .MuiTypography-root': {
    fontWeight: '700',
    color: theme.palette.mode === 'dark' ? theme.palette.primary.main : undefined,
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.primary.main,
    cursor: 'pointer',
  },
}));

const StyledSubItemLink = styled(Link)(({ theme }) => ({
  borderRadius: 0,
  padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  paddingLeft: theme.spacing(4),
  textTransform: 'capitalize',
  position: 'relative',
  cursor: 'pointer !important',
  width: '100%',
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : undefined,
  '&:hover': {
    cursor: 'pointer',
  },
}));

export {
  StyledCollapse,
  StyledItemLink,
  StyledListItem,
  StyledListItemText,
  StyledListItemWithLink,
  StyledListItemXs,
  StyledSubItem,
  StyledSubItemLink,
};
